'use client';

/**
 * ObeyaGraphCard — full-bleed entity graph (Reagraph) + RAG legend.
 *
 * Wraps the existing `<ProjectGraphReagraph />` so it paints from the
 * cards-everywhere LayoutEngine on `mode1`. Legend overlay matches the
 * legacy obeya widget — pinned bottom-left, server-driven labels via
 * `useObeyaProjects` (same React Query cache the rest of the obeya
 * cards use).
 *
 * Designed to be placed full-bleed (`x=0, y=0, w=12, h=12`) — the
 * graph canvas is absolutely sized and assumes it owns the viewport.
 *
 * The scene export lives in ProjectGraphReagraph now (it's the one with
 * `data` — see its own comment), not here. This wrapper used to also
 * call useCardExport with a bare {project_id} placeholder, producing two
 * scene entries at the same slot once a project was selected. `Empty`
 * below is a separate component (not a conditional hook call — rules of
 * hooks) so exactly one of {idle export, real export} is ever mounted.
 *
 * @see klyde_edge/composition/cards/obeya.py — content_key="obeya_graph"
 * @see klyde_ipe/components/widgets/project-graph-reagraph.tsx
 *
 * @component_role: Card
 * @component_type: Card Renderer
 * @domain: Canvas / Obeya
 * @layer: presentation
 */

import { AlertCircle, AlertTriangle, CheckCircle } from 'lucide-react';
import { ProjectGraphReagraph } from './project-graph-reagraph';
import { useMemo } from 'react';
import { useObeyaProjects } from '@/lib/api/hooks';
import { useCardExport } from '@/lib/design/card-export';
import { useCardIdentity } from '@/lib/design/card-chrome';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useLanguageStore } from '@/lib/stores/language-store';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';

export function ObeyaGraphCard() {
  // `<ProjectGraphReagraph />` owns its own fetch
  // (`graph_explore.project_visualize`). The card only threads
  // identifiers through. projectId from the viewport selection;
  // optional name from the graph-selection-store's projectContext
  // (set when the user picks a project, no extra fetch).
  const { projectId } = useViewportSelection();
  const projectName = useGraphSelectionStore(
    (s) => s.projectContext?.name ?? null,
  );

  // Server-driven legend labels (en/zh). `useObeyaProjects` is
  // deduped by React Query — the explorer's mode-toggle section
  // already runs the exact same query (same key: `obeya_list`,
  // limit:50, lang). Subscribing here costs zero network; we just
  // share the cache. The cleaner long-term shape is per-card label
  // metadata travelling through the matrix (parallel to
  // `tab_labels`); until that lands, sharing the cache is the right
  // call — i18n preserved, no extra fetch.
  const listStrategy = useCapabilityStore((s) => s.strategyName('obeya', 'list'));
  const lang = useLanguageStore((s) => s.lang);
  const { data: labelsData } = useObeyaProjects(listStrategy!, {
    limit: 50,
    ...(lang ? { lang } : {}),
  });
  const L = labelsData?.labels ?? {};

  if (!projectId) return <Empty msg="Select a project" />;

  return (
    <div className="relative h-full w-full overflow-hidden">
      <ProjectGraphReagraph
        projectId={projectId}
        projectName={projectName ?? undefined}
        depth={2}
        includeConversations={false}
      />

      <div className="absolute bottom-4 left-4 bg-background/80 backdrop-blur-sm rounded-lg p-3 border border-border/50 text-xs">
        <div className="font-medium mb-2">{L.status_legend ?? 'Status'}</div>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-3 h-3 text-red-500" />
            <span>{L.overdue_blocked ?? 'Overdue / blocked'}</span>
          </div>
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-3 h-3 text-amber-500" />
            <span>{L.at_risk ?? 'At risk'}</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-3 h-3 text-green-500" />
            <span>{L.on_track ?? 'On track'}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Registers the scene export only while nothing is selected — the
 *  "loaded" export takes over once ProjectGraphReagraph mounts, and this
 *  one unmounts (cleanup removes it from the registry), so the two never
 *  coexist. */
function Empty({ msg }: { msg: string }) {
  const { contentKey } = useCardIdentity();
  useCardExport(useMemo(() => ({
    content_key: contentKey ?? 'obeya_graph',
    status: 'idle' as const,
    query_key: null,
    text: 'Project graph',
    json: { project_id: null },
  }), [contentKey]));

  return (
    <div className="flex items-center justify-center h-full text-xs text-muted-foreground">
      {msg}
    </div>
  );
}
